import type { FeatureCollection } from 'geojson';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { registerIcons, STATION_ICON, STATION_ICON_SELECTED, STATION_SIGN_SIZE } from './icons';
import { fc, fillLayer, GeoJsonLayer, labelLayer, lineLayer } from './layers';
import { boundsOf, MapView, useMap } from './map-view';

/** `station`: points drawn as the bus stop sign, as on the other maps. */
type Kind = 'line' | 'polygon' | 'point' | 'station';

/** Fits the map to the data when it first arrives, and again whenever `fitKey` changes. */
function FitTo({ data, fitKey }: { data: FeatureCollection; fitKey?: string }) {
  const map = useMap();
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    const key = fitKey ?? '';
    if (!map || fitted.current === key || !data.features.length) return;
    const bounds = boundsOf(
      data.features.map((f) => ('coordinates' in f.geometry ? f.geometry.coordinates : [])),
    );
    if (!bounds) return;
    fitted.current = key;
    map.fitBounds(bounds, { padding: 40, duration: fitKey === undefined ? 0 : 400, maxZoom: 15 });
  }, [map, data, fitKey]);
  return null;
}

const HIGHLIGHT = '#f5b700';

function layersFor(kind: Kind) {
  if (kind === 'station')
    return [
      {
        type: 'symbol' as const,
        layout: { 'icon-image': STATION_ICON, 'icon-size': STATION_SIGN_SIZE, 'icon-allow-overlap': true },
      },
      { ...labelLayer('name'), minzoom: 14 },
    ];
  if (kind === 'line') return [lineLayer(['get', 'color'], 4, 0.85)];
  if (kind === 'polygon') return [fillLayer(['get', 'color'], 0.18), lineLayer(['get', 'color'], 2, 0.9)];
  return [
    {
      type: 'circle' as const,
      paint: {
        'circle-radius': 6,
        'circle-color': ['get', 'color'] as never,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    },
    labelLayer('name'),
  ];
}

function highlightLayers(kind: Kind) {
  if (kind === 'station')
    return [
      {
        type: 'symbol' as const,
        layout: {
          'icon-image': STATION_ICON_SELECTED,
          'icon-size': STATION_SIGN_SIZE,
          'icon-allow-overlap': true,
        },
      },
    ];
  if (kind === 'line') return [lineLayer(HIGHLIGHT, 9, 0.55), lineLayer(['get', 'color'], 5, 1)];
  if (kind === 'polygon') return [fillLayer(HIGHLIGHT, 0.3), lineLayer(HIGHLIGHT, 4, 1)];
  return [
    {
      type: 'circle' as const,
      paint: {
        'circle-radius': 11,
        'circle-color': HIGHLIGHT,
        'circle-opacity': 0.45,
        'circle-stroke-color': HIGHLIGHT,
        'circle-stroke-width': 2,
      },
    },
  ];
}

/** Adds the canvas icons (station signs) to the map. */
function Icons() {
  const map = useMap();
  useEffect(() => {
    if (map) registerIcons(map);
  }, [map]);
  return null;
}

/**
 * Map beside a list: every item drawn in its colour, the hovered row highlighted, and a click on an
 * item opens it. Features need `id`, `color` and (for labels) `name` properties.
 */
export function OverviewMap({
  data,
  kind,
  hovered,
  basePath,
  onHover,
  children,
  overlay,
  fitKey,
}: {
  data: FeatureCollection;
  kind: Kind;
  hovered?: string | null;
  basePath?: string;
  onHover?: (id: string | null) => void;
  /** Extra context layers drawn underneath (e.g. lines under stations). */
  children?: ReactNode;
  /** Controls shown on the map (top left). */
  overlay?: ReactNode;
  /** Re-fit the map to the data when this changes (e.g. the list's filters). */
  fitKey?: string;
}) {
  const navigate = useNavigate();
  const selected = useMemo(
    () => fc(hovered ? data.features.filter((f) => f.properties?.id === hovered) : []),
    [data, hovered],
  );
  return (
    <MapView className="h-full">
      <Icons />
      {children}
      <GeoJsonLayer
        data={data}
        layers={layersFor(kind)}
        onClick={basePath ? (p) => navigate(`${basePath}/${p.id}`) : undefined}
        onHover={onHover ? (p) => onHover(p ? String(p.id) : null) : undefined}
      />
      <GeoJsonLayer data={selected} layers={highlightLayers(kind)} />
      <FitTo data={data} fitKey={fitKey} />
      {overlay && (
        <div className="absolute top-2 left-2 z-10 rounded-lg bg-white/95 px-3 py-2 text-sm shadow-md ring-1 ring-slate-200">
          {overlay}
        </div>
      )}
    </MapView>
  );
}
